/**
 * Canonical license type options — shared across wizard and admin.
 *
 * The backend source of truth is `services/onboarding/shared/wizard_constants.py:LICENSE_TYPES`.
 * This frontend constant mirrors it and adds any types the admin UI historically
 * offered (e.g. "Psychologist") so existing saved data renders correctly.
 *
 * Keep sorted by rough clinical frequency; "Other" is always last.
 */
export const LICENSE_TYPES: readonly string[] = [
  'Addiction Counselor Candidate / Associate',
  'AMFT',
  'APRN',
  'BCBA',
  'CAC',
  'CADC',
  'CMA',
  'CNS',
  'DO',
  'LAC',
  'LADC',
  'LCMHC',
  'LCPC',
  'LCP',
  'LCSW',
  'LICSW',
  'LMSW',
  'LMFT',
  'LMHC',
  'LPN',
  'LP',
  'LPCC',
  'LPC',
  'LSW',
  'MA',
  'Marriage and Family Therapist Candidate / Associate',
  'MD',
  'PA-C',
  'PMHNP',
  'PMHNP-BC',
  'Professional Counselor Candidate / Associate',
  'Psychologist',
  'Psychologist Candidate',
  'Psychology Intern',
  'RPT',
  'RN',
  'Social Work Candidate / Associate',
  'Other',
] as const;

export const LICENSE_TYPE_LABELS: Readonly<Record<string, string>> = {
  AMFT: 'Associate Marriage and Family Therapist',
  APRN: 'Advanced Practice Registered Nurse',
  BCBA: 'Board Certified Behavior Analyst',
  CAC: 'Certified Addiction Counselor',
  CADC: 'Certified Alcohol and Drug Counselor',
  CMA: 'Certified Medical Assistant',
  CNS: 'Clinical Nurse Specialist',
  DO: 'Doctor of Osteopathic Medicine',
  LAC: 'Licensed Addiction Counselor',
  LADC: 'Licensed Alcohol and Drug Counselor',
  LCMHC: 'Licensed Clinical Mental Health Counselor',
  LCPC: 'Licensed Clinical Professional Counselor',
  LCP: 'Licensed Clinical Psychologist',
  LCSW: 'Licensed Clinical Social Worker',
  LICSW: 'Licensed Independent Clinical Social Worker',
  LMSW: 'Licensed Master Social Worker',
  LMFT: 'Licensed Marriage and Family Therapist',
  LMHC: 'Licensed Mental Health Counselor',
  LPN: 'Licensed Practical Nurse',
  LP: 'Licensed Psychologist',
  LPCC: 'Licensed Professional Clinical Counselor',
  LPC: 'Licensed Professional Counselor',
  LSW: 'Licensed Social Worker',
  MA: 'Master of Arts',
  MD: 'Doctor of Medicine',
  'PA-C': 'Physician Assistant-Certified',
  PMHNP: 'Psychiatric-Mental Health Nurse Practitioner',
  'PMHNP-BC': 'Psychiatric-Mental Health Nurse Practitioner-Board Certified',
  Psychologist: 'Psychologist',
  'RPT': 'Registered Play Therapist',
  RN: 'Registered Nurse',
};

export function formatLicenseTypeLabel(value: string): string {
  const fullName = LICENSE_TYPE_LABELS[value];
  return fullName && fullName !== value ? `${value} — ${fullName}` : value;
}
